import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { CheckRounded, CloseRounded, RestoreRounded, VisibilityOffOutlined } from '@mui/icons-material';
import { OUTLINE_TONES } from './DeckCanvas';
import DeckNumberField from './DeckNumberField';
import { INCLUDE_RULES, NOTES_MODES, REPEAT_UNITS, SLIDE_ROLES, outlineTone, shapeDisplayName } from './deckModel';

/**
 * Inspector for the selected slide: role, purpose, include and repeat rules, notes, the boxes on
 * it, and bulk accept or reject of its suggestions.
 * @param {object} props - Props.
 * @param {object} props.slide - Slide (from slidesOf).
 * @param {Array<object>} props.shapes - Shapes on the slide.
 * @param {Map<string, object>} props.fieldsByShape - Fields by shape key.
 * @param {number} props.suggestedCount - Suggestions on this slide.
 * @param {boolean} [props.readOnly] - No edits.
 * @param {function(object, string=): void} props.onChange - Edit the slide (patch, coalesce key).
 * @param {function(): void} props.onRestore - Bring a dropped slide back.
 * @param {function(): void} props.onDrop - Leave the slide out.
 * @param {function(): void} props.onAcceptSlide - Accept this slide's suggestions.
 * @param {function(): void} props.onRejectSlide - Reject this slide's suggestions.
 * @param {function(string): void} props.onSelectShape - Select a box.
 * @param {React.Ref} [props.headingRef] - The heading (focusable with tabIndex -1).
 * @returns {React.ReactElement}
 */
function SlideInspector({
  slide,
  shapes,
  fieldsByShape,
  suggestedCount,
  readOnly = false,
  onChange,
  onRestore,
  onDrop,
  onAcceptSlide,
  onRejectSlide,
  onSelectShape,
  headingRef,
}) {
  const idBase = `deck-slide-${String(slide.key).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const roles = slide.role && !SLIDE_ROLES.includes(slide.role) ? [slide.role, ...SLIDE_ROLES] : SLIDE_ROLES;
  const include = slide.excluded ? 'exclude' : (INCLUDE_RULES.some((r) => r.value === slide.include) ? slide.include : 'always');
  const repeat = slide.repeat && typeof slide.repeat === 'object' ? slide.repeat : {};
  const notes = slide.notes && typeof slide.notes === 'object' ? slide.notes : {};
  const layoutName = slide.layout?.name || slide.layoutName || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="slide-inspector">
      <Box>
        <Typography component="h3" ref={headingRef} tabIndex={-1} data-testid="slide-inspector-heading" sx={{ fontSize: 15, fontWeight: 600, outline: 'none' }}>Slide {slide.number}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {layoutName && <Chip size="small" variant="outlined" label={`Layout: ${layoutName}`} />}
          {slide.hidden && <Chip size="small" variant="outlined" icon={<VisibilityOffOutlined />} label="Hidden in PowerPoint" />}
        </Box>
      </Box>

      {slide.excluded && (
        <Alert
          severity="warning"
          action={!readOnly && (
            <Button size="small" color="inherit" startIcon={<RestoreRounded />} onClick={onRestore}>Restore</Button>
          )}
        >
          Left out of generated decks{slide.excludeReason ? `: ${slide.excludeReason}` : '.'}
        </Alert>
      )}

      {suggestedCount > 0 && !readOnly && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flex: '1 1 140px' }}>
            {suggestedCount} suggestion{suggestedCount === 1 ? '' : 's'} on this slide
          </Typography>
          <Button size="small" variant="outlined" startIcon={<CheckRounded />} onClick={onAcceptSlide}>Accept slide</Button>
          <Button size="small" color="inherit" startIcon={<CloseRounded />} onClick={onRejectSlide}>Reject slide</Button>
        </Box>
      )}

      <FormControl size="small" fullWidth disabled={readOnly}>
        <InputLabel id={`${idBase}-role-label`}>Role</InputLabel>
        <Select labelId={`${idBase}-role-label`} label="Role" value={roles.includes(slide.role) ? slide.role : 'content'} onChange={(event) => onChange({ role: event.target.value })}>
          {roles.map((role) => <MenuItem key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</MenuItem>)}
        </Select>
      </FormControl>

      <TextField
        id={`${idBase}-purpose`}
        label="Purpose"
        value={slide.purpose || ''}
        onChange={(event) => onChange({ purpose: event.target.value }, `${slide.key}:purpose`)}
        multiline
        minRows={2}
        maxRows={6}
        fullWidth
        InputProps={{ readOnly }}
        inputProps={{ maxLength: 600 }}
        helperText="What this slide is for in a generated deck."
      />

      <FormControl size="small" fullWidth disabled={readOnly}>
        <InputLabel id={`${idBase}-include-label`}>In generated decks</InputLabel>
        <Select
          labelId={`${idBase}-include-label`}
          label="In generated decks"
          value={include}
          onChange={(event) => {
            if (event.target.value === 'exclude') onDrop();
            else onChange({ include: event.target.value });
          }}
        >
          {INCLUDE_RULES.map((rule) => <MenuItem key={rule.value} value={rule.value}>{rule.label}</MenuItem>)}
        </Select>
      </FormControl>

      {include === 'repeat' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <DeckNumberField
            id={`${idBase}-repeat-min`}
            size="small"
            label="At least"
            value={Number.isFinite(repeat.min) ? repeat.min : null}
            min={0}
            onCommit={(n) => onChange({ repeat: { min: n === null ? 0 : n } }, `${slide.key}:repeat-min`)}
            InputProps={{ readOnly }}
            helperText=" "
          />
          <DeckNumberField
            id={`${idBase}-repeat-max`}
            size="small"
            label="At most"
            value={Number.isFinite(repeat.max) ? repeat.max : null}
            min={1}
            // The model keeps At most at or above At least (what is saved); say so while typing.
            onCommit={(n) => onChange({ repeat: { max: n === null ? Math.max(1, repeat.min || 1) : n } }, `${slide.key}:repeat-max`)}
            draftHelper={(draft) => (Number.isFinite(repeat.min) && /^\d+$/.test(String(draft).trim()) && Number(draft) < repeat.min ? `Raised to the minimum (${repeat.min}).` : '')}
            InputProps={{ readOnly }}
            helperText=" "
          />
          <FormControl size="small" fullWidth disabled={readOnly} sx={{ gridColumn: '1 / -1' }}>
            <InputLabel id={`${idBase}-repeat-unit-label`}>One slide per</InputLabel>
            <Select labelId={`${idBase}-repeat-unit-label`} label="One slide per" value={REPEAT_UNITS.some((u) => u.value === repeat.unit) ? repeat.unit : 'ai'} onChange={(event) => onChange({ repeat: { unit: event.target.value } })}>
              {REPEAT_UNITS.map((unit) => <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      )}

      <FormControl size="small" fullWidth disabled={readOnly}>
        <InputLabel id={`${idBase}-notes-label`}>Speaker notes</InputLabel>
        <Select labelId={`${idBase}-notes-label`} label="Speaker notes" value={NOTES_MODES.some((m) => m.value === notes.mode) ? notes.mode : 'keep'} onChange={(event) => onChange({ notes: { mode: event.target.value } })}>
          {NOTES_MODES.map((mode) => <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>)}
        </Select>
      </FormControl>
      {notes.mode === 'ai' && (
        <TextField
          id={`${idBase}-notes-prompt`}
          label="Notes prompt"
          value={notes.instruction || ''}
          onChange={(event) => onChange({ notes: { instruction: String(event.target.value).slice(0, 1000) } }, `${slide.key}:notes`)}
          multiline
          minRows={2}
          fullWidth
          InputProps={{ readOnly }}
          inputProps={{ maxLength: 1000 }}
        />
      )}

      <Box>
        <Typography component="h4" sx={{ fontSize: 13, fontWeight: 600 }}>Boxes on this slide</Typography>
        {shapes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No boxes were found on this slide.</Typography>
        ) : (
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {shapes.map((shape, index) => {
              const tone = outlineTone(shape, fieldsByShape.get(shape.key));
              const look = OUTLINE_TONES[tone];
              const { Icon } = look;
              return (
                <ListItemButton key={shape.key} onClick={() => onSelectShape(shape.key)} sx={{ borderRadius: 'var(--q-radius-sm)', gap: 1 }}>
                  <Box aria-hidden="true" sx={{ width: 12, height: 12, flex: 'none', border: `2px ${look.style} ${look.color}`, borderRadius: '2px' }} />
                  <Icon aria-hidden="true" sx={{ fontSize: 14, color: 'text.secondary', flex: 'none' }} />
                  <ListItemText
                    primary={`${index + 1}. ${shapeDisplayName(shape, index + 1)}`}
                    secondary={look.label}
                    primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}

export default SlideInspector;
